import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import HowToPlay from "@/components/HowToPlay";
import QuestionExtras from "@/components/QuestionExtras";
import { Fire, ArrowRight, Clock, CheckCircle, Sparkle, Scales, Crown } from "@phosphor-icons/react";
import { tick, whoosh } from "@/lib/audio";

const API_ROOT = process.env.REACT_APP_BACKEND_URL || "";

export default function Survival() {
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [question, setQuestion] = useState(null);
  const [streak, setStreak] = useState(0);
  const [room, setRoom] = useState(1);
  const [roomLabel, setRoomLabel] = useState("Knowledge");
  const [roomCorrect, setRoomCorrect] = useState(0);
  const [roomSize, setRoomSize] = useState(5);
  const [totalRooms, setTotalRooms] = useState(9);
  const [theme, setTheme] = useState(null);
  const [escaped, setEscaped] = useState(false);
  const [seasonEarned, setSeasonEarned] = useState(0);
  const [ended, setEnded] = useState(null);
  const [endsAt, setEndsAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [localPick, setLocalPick] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lifelinesUsed, setLifelinesUsed] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [aiHint, setAiHint] = useState(null);
  const timedOutFiredRef = useRef(false);
  const lastTickAtRef = useRef(11);
  const localPickRef = useRef(null);
  useEffect(() => { localPickRef.current = localPick; }, [localPick]);
  const nkPlus = !!user?.nk_plus_active;

  useEffect(() => {
    if (!question) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [question]);

  useEffect(() => {
    if (!question || !endsAt) return;
    if (timedOutFiredRef.current) return;
    const remainingMs = new Date(endsAt).getTime() - Date.now();
    const remainingSec = Math.ceil(remainingMs / 1000);
    if (remainingSec > 0 && remainingSec <= 3 && remainingSec !== lastTickAtRef.current) {
      lastTickAtRef.current = remainingSec;
      tick();
    }
    if (remainingMs <= 0) {
      timedOutFiredRef.current = true;
      whoosh();
      submitAnswer(localPickRef.current ?? -1, true);
    }
  }, [now, question, endsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const secondsLeft = endsAt
    ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 1000))
    : null;

  const start = async () => {
    try {
      const { data } = await api.post("/survival/start");
      setRun(data.run_id);
      setQuestion(data.question);
      setStreak(data.streak);
      setSeasonEarned(0);
      setEnded(null);
      setEndsAt(data.question_ends_at || null);
      setLocalPick(null);
      setLifelinesUsed(data.lifelines_used || []);
      setRemoved([]);
      setAiHint(null);
      // NKognito 2.0 — Escape room + theme
      setRoom(data.room || 1);
      setRoomLabel(data.room_label || "Knowledge");
      setRoomCorrect(data.room_correct || 0);
      setRoomSize(data.room_size || 5);
      setTotalRooms(data.total_rooms || 9);
      setTheme(data.theme || null);
      setEscaped(false);
      timedOutFiredRef.current = false;
      lastTickAtRef.current = 11;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start");
    }
  };

  const useLifeline = async (kind) => {
    if (!run) return;
    if (!nkPlus) {
      toast("Lifelines are an NK+ perk", {
        description: "Upgrade in Profile → NK+ to unlock 50:50 and AI Help.",
      });
      return;
    }
    try {
      const { data } = await api.post("/survival/lifeline", { run_id: run, kind });
      setLifelinesUsed(data.lifelines_used || []);
      if (data.kind === "fifty") {
        const remove = data.remove_indices || [];
        setRemoved(remove);
        setLocalPick((cur) => (cur !== null && remove.includes(cur) ? null : cur));
        toast.success("50:50 — two wrong answers removed");
      } else if (data.kind === "ai") {
        setAiHint({
          recommended_index: data.recommended_index,
          confidence: data.confidence,
          reason: data.reason,
        });
        toast.success(`AI Help — recommends option ${String.fromCharCode(65 + data.recommended_index)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lifeline failed");
    }
  };

  const togglePick = (i) => {
    if (submitting) return;
    setLocalPick((cur) => (cur === i ? null : i));
  };

  const submitAnswer = async (i, isTimeout = false) => {
    if (!run || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/survival/answer", {
        run_id: run,
        option_index: i === null ? -1 : i,
      });
      // Backend ends the run on the first wrong/timeout — `data.correct` flips false
      // and no `question` is returned.
      if (!data.correct) {
        setEnded({
          ...data,
          timed_out: data.timed_out || isTimeout,
        });
        setQuestion(null);
        setEndsAt(null);
        setLocalPick(null);
        return;
      }
      setQuestion(data.question);
      setStreak(data.streak);
      setSeasonEarned((s) => s + (data.season_score_earned || 0));
      setEndsAt(data.question_ends_at || null);
      setLocalPick(null);
      setRemoved([]);
      setAiHint(null);
      // NKognito 2.0 — sync room state
      if (data.room) setRoom(data.room);
      if (data.room_label) setRoomLabel(data.room_label);
      if (data.room_correct != null) setRoomCorrect(data.room_correct);
      if (data.room_advanced) {
        toast.success(`Room ${data.rooms_cleared?.[data.rooms_cleared.length - 1]} cleared! → ${data.room_label}`);
      }
      if (data.escaped) {
        setEscaped(true);
        toast.success(`🏆 ESCAPED! +${data.escape_bonus} bonus SS`);
        setEnded({ ...data, escaped: true });
        setQuestion(null);
        setEndsAt(null);
      }
      timedOutFiredRef.current = false;
      lastTickAtRef.current = 11;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  // Active gameplay
  if (question) {
    const pct = secondsLeft != null ? (secondsLeft / 15) * 100 : 100;
    const urgency = secondsLeft != null && secondsLeft <= 3 ? "urgent"
                  : secondsLeft != null && secondsLeft <= 5 ? "warn" : "normal";
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium">NK Escape</p>
            {theme && (
              <p className="text-[10px] font-mono text-white/40 mt-0.5" style={{color: theme.accent}}>
                {theme.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 font-mono text-sm">
            <span className="text-white/55">Streak <span className="text-nk-gold font-bold">{streak}</span></span>
            <span className="text-white/55">+<span className="text-nk-gold">{seasonEarned}</span> SS</span>
          </div>
        </div>

        {/* Room progress — vertical rooms indicator */}
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="escape-rooms">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-mono uppercase text-white/60">
              Room {room} of {totalRooms} · <span style={{color: theme?.accent || "#F5C518"}}>{roomLabel}</span>
            </p>
            <p className="text-xs font-mono text-white/50">
              {roomCorrect}/{roomSize} correct in room
            </p>
          </div>
          <div className="flex gap-1">
            {Array.from({length: totalRooms}).map((_, idx) => {
              const n = idx + 1;
              const cleared = n < room;
              const active = n === room;
              return (
                <div key={n} className={`flex-1 h-2 rounded-sm transition-all ${
                  cleared ? "bg-nk-gold" : active ? "bg-nk-gold/40 animate-pulse" : "bg-white/8"
                }`} data-testid={`room-indicator-${n}`} />
              );
            })}
          </div>
        </div>

        {/* Timer bar */}
        <div className="space-y-1.5" data-testid="survival-timer">
          <div className="flex items-center justify-between text-[11px] font-mono">
            <span className="flex items-center gap-1 text-white/55">
              <Clock size={11} weight="duotone" /> Time
            </span>
            <span className={`tabular-nums font-display ${
              urgency === "urgent" ? "text-nk-bad" : urgency === "warn" ? "text-nk-warn" : "text-nk-gold"
            }`}>
              {secondsLeft != null ? `${secondsLeft}s` : "—"}
            </span>
          </div>
          <div className="h-1.5 nk-timer-bar rounded-full overflow-hidden">
            <div
              className={`h-full nk-timer-fill ${urgency === "urgent" ? "nk-timer-urgent" : urgency === "warn" ? "nk-timer-warn" : ""}`}
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
          </div>
        </div>

        <div className="nk-board rounded-md p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.16em] text-nk-gold/70 font-mono mb-4">{question.category}</p>
          <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-6 text-white">{question.body}</p>

          <QuestionExtras extras={question.nke_extras} qid={question.qid} apiUrl={API_ROOT} />

          {/* NK+ Lifelines */}
          <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="survival-lifelines">
            <SurvivalLifelineBtn
              kind="fifty" label="50:50" Icon={Scales}
              used={lifelinesUsed.includes("fifty")}
              locked={!nkPlus}
              onUse={useLifeline}
            />
            <SurvivalLifelineBtn
              kind="ai" label="AI Help" Icon={Sparkle}
              used={lifelinesUsed.includes("ai")}
              locked={!nkPlus}
              onUse={useLifeline}
            />
            {!nkPlus && (
              <span className="text-[10px] text-nk-gold inline-flex items-center gap-1"
                    data-testid="survival-lifelines-upsell">
                <Crown size={10} weight="fill" /> NK+ perk
              </span>
            )}
          </div>

          {aiHint && (
            <div className="mb-4 p-3 rounded-md border border-nk-gold/40 bg-nk-gold/[0.06] flex items-start gap-2"
                 data-testid="survival-ai-hint">
              <Sparkle size={16} weight="fill" className="text-nk-gold shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-nk-gold font-mono">
                  AI recommends option {String.fromCharCode(65 + aiHint.recommended_index)}
                  {aiHint.confidence != null && (
                    <span className="opacity-70"> · {Math.round(aiHint.confidence * 100)}% confidence</span>
                  )}
                </p>
                {aiHint.reason && <p className="text-[11px] text-white/70 mt-1">{aiHint.reason}</p>}
              </div>
            </div>
          )}

          <div className="grid gap-3">
            {question.options.map((opt, i) => {
              const isSelected = localPick === i;
              const isRemoved = removed.includes(i);
              const isAiRec = aiHint?.recommended_index === i && !isRemoved;
              return (
                <button
                  key={i}
                  data-testid={`survival-option-btn-${i}`}
                  onClick={() => !isRemoved && togglePick(i)}
                  disabled={submitting || isRemoved}
                  aria-pressed={isSelected}
                  className={`nk-option text-left rounded-md px-4 py-4 flex items-center gap-3 ${
                    isRemoved ? "nk-option-dimmed line-through cursor-not-allowed" :
                    isSelected ? "nk-option-selected" :
                    isAiRec ? "ring-1 ring-nk-gold/50" : ""
                  }`}
                >
                  <span className={`font-mono text-xs w-5 ${isSelected ? "text-nk-gold" : "text-white/45"}`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {isAiRec && !isSelected && (
                    <Sparkle size={14} weight="fill" className="text-nk-gold/70 shrink-0" />
                  )}
                  {isSelected && <CheckCircle size={18} weight="fill" className="text-nk-gold shrink-0" />}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-white/55" data-testid="survival-pick-hint">
              {localPick === null
                ? "Pick an answer. You can change it until time runs out."
                : <>You picked <span className="font-mono text-nk-gold">{String.fromCharCode(65 + localPick)}</span>. Tap to change, or lock in.</>}
            </p>
            <button
              type="button"
              data-testid="survival-lockin-btn"
              onClick={() => submitAnswer(localPick, false)}
              disabled={localPick === null || submitting}
              className="nk-btn nk-btn-gold disabled:opacity-40 disabled:cursor-not-allowed text-xs px-4 py-2"
            >
              Lock in <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
        <p className="text-xs text-white/45 text-center">One miss ends the run.</p>
      </div>
    );
  }

  // End screen
  if (ended) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Fire size={32} weight="duotone" className="text-nk-bad" />
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium">Run ended {ended.timed_out && "· timed out"}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">
          You answered <span className="text-nk-gold">{ended.streak}</span> in a row.
        </h1>
        {ended.new_record && (
          <p className="text-nk-gold text-sm">★ New personal best!</p>
        )}
        <p className="text-white/65">You earned <span className="text-nk-gold font-mono">+{ended.season_score_earned}</span> Season Score this run.</p>
        <button
          onClick={start}
          className="nk-btn nk-btn-gold"
        >Try again <ArrowRight size={14} weight="bold" /></button>
      </div>
    );
  }

  // Start screen
  return (
    <div className="space-y-7 max-w-2xl">
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/80 font-medium mb-2">NK Escape</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          One miss. <span className="italic font-light text-nk-gold">You&apos;re out.</span>
        </h1>
        <p className="text-sm sm:text-base text-white/65 mt-3 max-w-md leading-relaxed">
          Endless run. Chase 10, 25, 50, 100 in a row.
        </p>
      </header>

      <button
        data-testid="survival-start-btn"
        onClick={start}
        className="nk-btn nk-btn-gold px-6 py-3.5 text-base"
      >
        Start a run <Fire size={16} weight="bold" />
      </button>

      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-3">Milestones</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[10, 25, 50, 100].map((m) => (
            <div key={m} className="nk-card p-4 text-center">
              <p className="font-display text-2xl sm:text-3xl font-medium text-nk-gold">{m}</p>
              <p className="text-xs text-white/55 mt-1">in a row</p>
            </div>
          ))}
        </div>
      </section>

      <HowToPlay
        title="How NK Escape works"
        steps={[
          { title: "Start a run", desc: "One question at a time. 15 seconds each." },
          { title: "One mistake ends it", desc: "Wrong answer or time out — run over." },
          { title: "Chase the milestones", desc: "Bonuses at 10, 25, 50, 100 in a row." },
        ]}
        points={[
          { label: "Correct", value: "+5 SS" },
          { label: "Every 5 streak", value: "+5 SS" },
          { label: "10 / 25 / 50 / 100", value: "+25/50/100/250" },
          { label: "Time / question", value: "15s" },
        ]}
      />
    </div>
  );
}

function SurvivalLifelineBtn({ kind, label, Icon, used, locked, onUse }) {
  return (
    <button
      data-testid={`survival-lifeline-${kind}`}
      onClick={() => !used && onUse(kind)}
      disabled={used}
      title={locked ? "NK+ members only" : used ? "Already used this run" : label}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        used
          ? "border-nk-line/30 text-nk-dim/50 line-through cursor-not-allowed"
          : locked
            ? "border-nk-gold/40 text-nk-gold/70 hover:bg-nk-gold/10"
            : "border-nk-gold text-nk-gold hover:bg-nk-gold/10"
      }`}
    >
      <Icon size={12} weight="duotone" />
      {label}
      {locked && !used && <Crown size={10} weight="fill" className="opacity-80" />}
    </button>
  );
}
